import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { GetMasterDataPurchasersDTO } from "@src/domain/dto/masterData";
import type { MasterDataPurchaser } from "@src/domain/entities/masterData";
import { GetMasterDataPurchasersUseCase } from "@src/application/useCases/masterData";
import { MasterDataPurchaserRepository } from "@src/infrastructure/repositories/masterData";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  getMasterDataListCacheEntry,
  masterDataAllListCacheKey,
  setMasterDataListCacheEntry,
} from "./masterData/masterDataListCache";

interface MasterDataPurchasersDataContextValue {
  brandKey: string;
  purchasers: MasterDataPurchaser[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  refetch: () => void;
}

const MasterDataPurchasersDataContext =
  createContext<MasterDataPurchasersDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataPurchasers(): MasterDataPurchasersDataContextValue {
  const context = useContext(MasterDataPurchasersDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataPurchasers must be used within MasterDataPurchasersDataProvider",
    );
  }
  return context;
}

interface MasterDataPurchasersDataProviderProps {
  brandKey: string;
  children: ReactNode;
}

/**
 * Loads all matching purchasers for the brand (lookup-style: items only).
 * Tenant DB is selected via X-Brand-Key on MasterDataGraphQlAdapter.
 */
export function MasterDataPurchasersDataProvider({
  brandKey,
  children,
}: Readonly<MasterDataPurchasersDataProviderProps>) {
  const getPurchasersUseCase = useMemo(
    () => new GetMasterDataPurchasersUseCase(new MasterDataPurchaserRepository()),
    [],
  );

  const initialCacheKey = masterDataAllListCacheKey("purchasers", brandKey, "");
  const initialCached =
    getMasterDataListCacheEntry<MasterDataPurchaser>(initialCacheKey);

  const [purchasers, setPurchasers] = useState<MasterDataPurchaser[]>(
    () => initialCached?.items ?? [],
  );
  const [loading, setLoading] = useState(!initialCached);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const lastReloadTokenRef = useRef(0);

  const refetch = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    MasterDataGraphQlAdapter.getInstance().setBrandKey(brandKey);
    return () => {
      MasterDataGraphQlAdapter.getInstance().setBrandKey(null);
    };
  }, [brandKey]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = masterDataAllListCacheKey("purchasers", brandKey, search);
    const forceRefresh = reloadToken !== lastReloadTokenRef.current;
    const cached =
      getMasterDataListCacheEntry<MasterDataPurchaser>(cacheKey);

    if (cached && !forceRefresh) {
      setPurchasers(cached.items);
      setLoading(false);
      setError(null);
      return;
    }

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await getPurchasersUseCase.execute(
          new GetMasterDataPurchasersDTO({
            includeInactive: true,
            search: search.trim() || null,
          }),
        );
        if (cancelled) return;
        // Shared cache entry shape still includes pagination fields; derive locally.
        setMasterDataListCacheEntry(cacheKey, {
          items: result.items,
          total: result.items.length,
          page: 1,
          limit: result.items.length,
        });
        lastReloadTokenRef.current = reloadToken;
        setPurchasers(result.items);
      } catch (err) {
        if (cancelled) return;
        setPurchasers([]);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load master data purchasers",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [brandKey, getPurchasersUseCase, reloadToken, search]);

  const value = useMemo(
    (): MasterDataPurchasersDataContextValue => ({
      brandKey,
      purchasers,
      loading,
      error,
      search,
      setSearch,
      refetch,
    }),
    [brandKey, purchasers, loading, error, search, setSearch, refetch],
  );

  return (
    <MasterDataPurchasersDataContext.Provider value={value}>
      {children}
    </MasterDataPurchasersDataContext.Provider>
  );
}
